import { MapPin, X, SlidersHorizontal } from 'lucide-react';
import { SearchBar } from './SearchBar';
import { cn } from '@/lib/job-board/utils';

export interface FilterState {
  q: string;
  location: string;
  source: string;
  remote: boolean;
}

interface FilterBarProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  className?: string;
}

export function FilterBar({ filters, onChange, className }: FilterBarProps) {
  const hasActiveFilters =
    filters.q || filters.location || filters.source || filters.remote;

  const handleClearFilters = () => {
    onChange({
      q: '',
      location: '',
      source: '',
      remote: false,
    });
  };

  return (
    <section
      className={cn(
        'rounded-[2rem] border border-white/10 p-5 sm:p-6 shadow-[0_32px_90px_-40px_rgba(0,0,0,0.92)] backdrop-blur-2xl',
        'bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))]',
        className
      )}
    >
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04]">
            <SlidersHorizontal className="h-3.5 w-3.5 text-[#06b6d4]" aria-hidden="true" />
          </div>
          <span className="text-[11px] font-medium uppercase tracking-[0.24em] text-white/55">
            Filters
          </span>
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="text-xs font-medium text-white/50 transition-colors hover:text-[#06b6d4]"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Primary input row */}
      <div className="flex flex-col gap-3 sm:flex-row">
        {/* Search — dominant */}
        <div className="flex-1 min-w-0">
          <SearchBar
            value={filters.q}
            onChange={(q) => onChange({ ...filters, q })}
            placeholder="Search by title, company, or keywords..."
          />
        </div>

        {/* Location — secondary */}
        <div className="relative sm:w-64">
          <MapPin
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35"
            aria-hidden="true"
          />
          <input
            type="text"
            placeholder="Location"
            value={filters.location}
            onChange={(e) => onChange({ ...filters, location: e.target.value })}
            aria-label="Filter by location"
            className="h-12 w-full rounded-xl border border-white/10 bg-[#0a0d14] pl-10 pr-4 text-sm text-white placeholder:text-white/35 transition-all focus:border-[#06b6d4]/50 focus:outline-none focus:ring-2 focus:ring-[#06b6d4]/15"
          />
        </div>
      </div>

      {/* Secondary controls row */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {/* Remote toggle */}
        <button
          type="button"
          onClick={() => onChange({ ...filters, remote: !filters.remote })}
          aria-pressed={filters.remote}
          className={cn(
            'inline-flex h-9 items-center gap-2 rounded-full border px-4 text-xs font-medium transition-all',
            filters.remote
              ? 'border-[#06b6d4]/60 bg-[#06b6d4]/15 text-[#06b6d4] shadow-[0_0_20px_-8px_rgba(6,182,212,0.6)]'
              : 'border-white/10 bg-white/[0.04] text-white/65 hover:border-white/25 hover:bg-white/[0.07] hover:text-white'
          )}
        >
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full transition-colors',
              filters.remote ? 'bg-[#06b6d4]' : 'bg-white/30'
            )}
            aria-hidden="true"
          />
          Remote only
        </button>

        {/* Active filter chips */}
        {filters.q && (
          <FilterChip
            label={`Search: ${filters.q.slice(0, 24)}${filters.q.length > 24 ? '...' : ''}`}
            onClose={() => onChange({ ...filters, q: '' })}
          />
        )}
        {filters.location && (
          <FilterChip
            label={`Location: ${filters.location}`}
            onClose={() => onChange({ ...filters, location: '' })}
          />
        )}
      </div>
    </section>
  );
}

function FilterChip({ label, onClose }: { label: string; onClose: () => void }) {
  return (
    <span className="inline-flex h-9 items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] pl-3 pr-1.5 text-xs text-white/85">
      {label}
      <button
        type="button"
        onClick={onClose}
        className="flex h-6 w-6 items-center justify-center rounded-full text-white/40 transition-colors hover:bg-white/10 hover:text-white"
        aria-label={`Remove ${label}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}